import translations from '../../../translations';
import ButtonDefault from '../../buttons/ButtonDefault';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import DependencyTable from '../../generic/DependencyTable';
import GenericModal from '../../generic/modal/GenericModal';
import ModalBody from '../../generic/modal/ModalBody';
import ModalFooter from '../../generic/modal/ModalFooter';
import ModalHeader from '../../generic/modal/ModalHeader';
import ImportFlowForm from './ImportFlowForm';
import useFlowImport from './useFlowImport';

type Props = {
    show: boolean;
    onHide: () => void;
    modalContainer: HTMLElement | null | undefined;
    onImportSuccess?: () => void;
};

const ImportFlowModal = ({ show, onHide, modalContainer, onImportSuccess }: Props) => {
    const onClose = () => {
        onDismissConfirmation(); // Ensure the confirmation state is reset
        onHide();
    };

    const onImportComplete = () => {
        onImportSuccess?.();
        onClose();
    };

    const {
        isProcessing,
        importToken,
        showImportConfirmation,
        conflictedImportDependents,
        showConflictedImportDependents,
        confirmFlowImportMessage,
        onImportConfirmed,
        onChangeImportToken,
        onDrop,
        onImportToken,
        onDismissConfirmation,
    } = useFlowImport(onImportComplete);

    const UI = showConflictedImportDependents
        ? 'conflicts'
        : showImportConfirmation
          ? 'confirmation'
          : 'form';

    return (
        <GenericModal
            show={show}
            onHide={onClose}
            container={modalContainer}
            className="modal-dialog-small"
        >
            {UI === 'form' && (
                <>
                    <ModalHeader onHide={onClose}>
                        <h4 className="modal-title">
                            {translations.IMPORT_EXPORT_import_flow_section_heading}
                        </h4>
                    </ModalHeader>
                    <ModalBody>
                        <ImportFlowForm
                            isProcessing={isProcessing}
                            importToken={importToken}
                            onChangeImportToken={onChangeImportToken}
                            onImportToken={onImportToken}
                            onDrop={onDrop}
                        />
                    </ModalBody>
                </>
            )}
            {UI === 'confirmation' && (
                <>
                    <ModalHeader onHide={onClose}>{translations.IMPORT_confirm_title}</ModalHeader>
                    <ModalBody>
                        <p>{confirmFlowImportMessage}</p>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonDefault onClick={onDismissConfirmation}>
                            {translations.COMMON_cancel}
                        </ButtonDefault>
                        <ButtonPrimary
                            onClick={() => {
                                onImportConfirmed(false);
                            }}
                            disabled={isProcessing}
                        >
                            {translations.COMMON_confirm}
                        </ButtonPrimary>
                    </ModalFooter>
                </>
            )}
            {UI === 'conflicts' && (
                <>
                    <ModalHeader onHide={onClose}>
                        <h4 className="modal-title">
                            {translations.IMPORT_confirm_overwrite_title}
                        </h4>
                    </ModalHeader>
                    <ModalBody>
                        <p>{translations.IMPORT_confirm_overwrite_body_1}</p>
                        <p>{translations.IMPORT_confirm_overwrite_body_2}</p>
                        {conflictedImportDependents && (
                            <DependencyTable dependents={conflictedImportDependents} />
                        )}
                        <p>{translations.IMPORT_confirm_overwrite_body_3}</p>
                        <p>{translations.IMPORT_confirm_overwrite_body_4}</p>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonDefault onClick={onDismissConfirmation}>
                            {translations.COMMON_cancel}
                        </ButtonDefault>
                        <ButtonPrimary
                            onClick={() => {
                                onImportConfirmed(true);
                            }}
                            disabled={isProcessing}
                        >
                            {translations.COMMON_confirm}
                        </ButtonPrimary>
                    </ModalFooter>
                </>
            )}
        </GenericModal>
    );
};

export default ImportFlowModal;
